import { create } from 'zustand';
import { axiosClient } from '@/api/axiosClient';
import { queryClient } from '@/api/queryClient';
import {
  User,
  AuthResponse,
  getStoredToken,
  getStoredUser,
  setStoredToken,
  setStoredRefreshToken,
  setStoredUser,
  clearAuthData,
} from '@/lib/auth';
import { useLanguageStore } from './languageStore';
import { trackEvent, identifyUser } from '@/lib/analytics';

interface AuthState {
  user: User | null;
  loading: boolean;
  initialized: boolean;
  initialize: () => void;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, phone?: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  initialized: false,

  initialize: () => {
    const token = getStoredToken();
    const storedUser = getStoredUser();

    if (token && storedUser) {
      // Trust localStorage initially - show UI immediately
      set({ user: storedUser, loading: false, initialized: true });

      // Sync language from stored user
      useLanguageStore.getState().syncFromUser(storedUser.language);

      // Identify user for analytics (returning user, with hashed email for FB Pixel)
      identifyUser(storedUser.uid, { email: storedUser.email, name: storedUser.name });

      // Validate token in background via Hub SSO
      axiosClient
        .get<User>('/hub/auth/me')
        .then((response) => {
          const user = response.data;
          setStoredUser(user);
          set({ user });
          // Sync language from backend (in case it changed)
          useLanguageStore.getState().syncFromUser(user.language);
        })
        .catch((error) => {
          // Only logout on explicit 401 (invalid/expired token)
          // Network errors shouldn't force logout - user stays logged in
          if (error.response?.status === 401) {
            clearAuthData();
            set({ user: null });
          }
        });
    } else {
      clearAuthData();
      set({ user: null, loading: false, initialized: true });
    }
  },

  login: async (email: string, password: string) => {
    // Clear any previous user data including last project
    clearAuthData();
    queryClient.clear();

    // Login via Angular Hub SSO
    const response = await axiosClient.post<AuthResponse>('/hub/auth/login', {
      email: email.toLowerCase(),
      password,
    });

    const { access_token, refresh_token, user } = response.data;
    setStoredToken(access_token);
    setStoredRefreshToken(refresh_token);
    setStoredUser(user);
    set({ user });
    // Sync language from user
    useLanguageStore.getState().syncFromUser(user.language);

    // Track login event and identify user (with hashed email for FB Pixel)
    trackEvent('login', { method: 'email' });
    identifyUser(user.uid, { email: user.email, name: user.name });
  },

  register: async (email: string, password: string, name: string, phone?: string) => {
    // Clear any previous user data including last project
    clearAuthData();

    const response = await axiosClient.post<AuthResponse>('/hub/auth/register', {
      email: email.toLowerCase(),
      password,
      password2: password,
      first_name: name.split(' ')[0],
      last_name: name.split(' ').slice(1).join(' '),
      phone,
    });

    const { access_token, refresh_token, user } = response.data;
    setStoredToken(access_token);
    setStoredRefreshToken(refresh_token);
    setStoredUser(user);
    set({ user });
    // Sync language from user (new users get default language)
    useLanguageStore.getState().syncFromUser(user.language);

    // Track sign_up event and identify user (with hashed email for FB Pixel)
    trackEvent('sign_up', { method: 'email' });
    identifyUser(user.uid, { email: user.email, name: user.name });
  },

  logout: () => {
    clearAuthData();
    queryClient.clear();
    set({ user: null });
  },

  setUser: (user) => set({ user }),
}));
